import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesExecutionForm,
  selectGuaranteesExecutionFormError,
  selectGuaranteesExecutionFormStatus,
  selectGuaranteesRejectApplicationByUakdoControllerError,
  selectGuaranteesRejectApplicationByUakdoControllerStatus,
  selectGuaranteesSubmitApplicationByUakdoControllerError,
  selectGuaranteesSubmitApplicationByUakdoControllerStatus,
  selectGuaranteesUakdoSupervisorInfoStatus,
  selectHasGuaranteesUakdoControllerAccess,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesExecutionForm,
  fetchGuaranteesUakdoSupervisorInfo,
  rejectGuaranteesApplicationByUakdoController,
  submitGuaranteesApplicationByUakdoController,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesApplicationStatus, GuaranteesFetchStatus } from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import { getGuaranteesUakdoSecurityType } from '../step7/utils.ts';
import { UAKDO_CONTROLLER_CONTROL_TEXT } from './constants.ts';

const UAKDO_CONTROLLER_EDITABLE_STATUSES = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL,
  GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL,
]);

export const useUakdoControllerControlPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const executionFormFromStore = useSelector(selectGuaranteesExecutionForm);
  const executionFormStatus = useSelector(selectGuaranteesExecutionFormStatus);
  const executionFormError = useSelector(selectGuaranteesExecutionFormError);
  const submitByUakdoControllerStatus = useSelector(
    selectGuaranteesSubmitApplicationByUakdoControllerStatus
  );
  const submitByUakdoControllerError = useSelector(
    selectGuaranteesSubmitApplicationByUakdoControllerError
  );
  const rejectByUakdoControllerStatus = useSelector(
    selectGuaranteesRejectApplicationByUakdoControllerStatus
  );
  const rejectByUakdoControllerError = useSelector(
    selectGuaranteesRejectApplicationByUakdoControllerError
  );
  const uakdoSupervisorInfoStatus = useSelector(selectGuaranteesUakdoSupervisorInfoStatus);
  const hasUakdoControllerAccess = useSelector(selectHasGuaranteesUakdoControllerAccess);
  const redirectPath = !contragentId ? '/guarantees/administrator/get-started' : '';
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const executionForm = isLoadedApplicationCurrent
    ? executionFormFromStore ?? currentRouteApplication?.executionForm ?? null
    : null;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number' ? currentRouteApplication.version : 0;
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const securityType = getGuaranteesUakdoSecurityType(currentRouteApplication);
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isExecutionFormLoading = executionFormStatus === GuaranteesFetchStatus.Loading;
  const isSubmittingByUakdoController =
    submitByUakdoControllerStatus === GuaranteesFetchStatus.Loading;
  const isRejectingByUakdoController =
    rejectByUakdoControllerStatus === GuaranteesFetchStatus.Loading;
  const isUakdoAccessLoading =
    uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Idle ||
    uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Loading;
  const isUakdoAccessDenied = !isUakdoAccessLoading && !hasUakdoControllerAccess;
  const isUakdoControllerControlEditable =
    UAKDO_CONTROLLER_EDITABLE_STATUSES.has(normalizedStatus);
  const requestError =
    applicationError ||
    executionFormError ||
    submitByUakdoControllerError ||
    rejectByUakdoControllerError;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate('/tasks'),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const navigateToTasks = useCallback(() => {
    navigate('/tasks', { replace: true });
  }, [navigate]);
  const handleCloseUakdoAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigateToTasks();
  }, [handleCloseAlertModal, navigateToTasks]);
  const handleConfirm = useCallback(async () => {
    if (!effectiveApplicationId || !currentApplicationVersion) {
      return;
    }

    try {
      await dispatch(
        submitGuaranteesApplicationByUakdoController({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: UAKDO_CONTROLLER_CONTROL_TEXT.confirmedMessage,
        buttonValue: UAKDO_CONTROLLER_CONTROL_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
      throw error;
    }
  }, [
    currentApplicationVersion,
    dispatch,
    effectiveApplicationId,
    navigateToTasks,
    setModalSuccess,
    showGuaranteesRequestError,
  ]);
  const handleReject = useCallback(
    async (comment: string) => {
      if (!effectiveApplicationId || !currentApplicationVersion) {
        return;
      }

      try {
        await dispatch(
          rejectGuaranteesApplicationByUakdoController({
            applicationId: effectiveApplicationId,
            version: currentApplicationVersion,
            payload: { comment },
          })
        ).unwrap();
        setModalSuccess({
          text: UAKDO_CONTROLLER_CONTROL_TEXT.rejectedMessage,
          buttonValue: UAKDO_CONTROLLER_CONTROL_TEXT.successCloseButton,
          onClick: navigateToTasks,
          onClose: navigateToTasks,
        });
      } catch (error) {
        showGuaranteesRequestError(error);
        throw error;
      }
    },
    [
      currentApplicationVersion,
      dispatch,
      effectiveApplicationId,
      navigateToTasks,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );

  useEffect(() => {
    if (uakdoSupervisorInfoStatus === GuaranteesFetchStatus.Idle) {
      void dispatch(fetchGuaranteesUakdoSupervisorInfo());
    }
  }, [dispatch, uakdoSupervisorInfoStatus]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (!applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesExecutionForm({ applicationId }));
  }, [applicationId, dispatch]);

  useEffect(() => {
    if (isUakdoAccessDenied) {
      setModalAlert({ text: UAKDO_CONTROLLER_CONTROL_TEXT.accessDeniedText });
    }
  }, [isUakdoAccessDenied, setModalAlert]);

  useFreshGuaranteesRequestError(requestError, showGuaranteesRequestError);

  return {
    contragentId,
    currentApplicationVersion,
    effectiveApplicationId,
    executionForm,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUakdoAccessModal,
    handleConfirm,
    handleReject,
    headerProps,
    isApplicationLoading,
    isExecutionFormLoading,
    isRejectingByUakdoController,
    isSubmittingByUakdoController,
    isUakdoAccessDenied,
    isUakdoAccessLoading,
    isUakdoControllerControlEditable,
    modalAlert,
    modalSuccess,
    redirectPath,
    securityType,
    showGuaranteesRequestError,
  };
};
